/**
 * The current league's URL base, e.g. `#/l/vitalli-masters`.
 *
 * Links inside a league are built from this so they stay within it — a fixture
 * link from Vitalli must never land in The Society Team League.
 */
export function leagueBase(): string {
  const parts = window.location.hash.slice(1).split('/').filter(Boolean);
  return parts[0] === 'l' && parts[1] ? `#/l/${parts[1]}` : '#';
}

/**
 * What an address in the hash means, and where the app should send it.
 *
 * Pulled out of the component because the decision it makes is the one that
 * broke: a captain's link opened on a league's own domain was redirected to
 * that league's homepage before the captain page could draw. An early return
 * in the component could not prevent it — the redirect lives in an effect, and
 * effects run after render whether or not a branch below them was taken.
 *
 * Kept pure so the rule can be checked without a browser.
 */

export interface Route {
  /** #/l/<slug>/<section>/<param> */
  inLeague: boolean;
  leagueSlug: string | null;
  section: string;
  param: string | undefined;
  /** #/s/<token> — a captain's link, which belongs to no league page. */
  captainToken: string | null;
}

export function parseRoute(hash: string): Route {
  const parts = hash.split('/').filter(Boolean);
  const inLeague = parts[0] === 'l' && Boolean(parts[1]);
  return {
    inLeague,
    leagueSlug: inLeague ? parts[1] : null,
    section: inLeague ? (parts[2] ?? '') : '',
    param: inLeague ? parts[3] : undefined,
    captainToken: parts[0] === 's' && parts[1] ? parts[1] : null,
  };
}

/**
 * The hash a league's own domain should be sent to, or null to stay put.
 *
 * A league that brings its own domain opens on that league: arriving at the
 * root of thepadelsocietysa.co.za lands on the Society rather than on a list
 * of leagues.
 *
 * A CAPTAIN'S LINK IS THE EXCEPTION, and it is the whole reason this function
 * exists. `#/s/<token>` carries its own league inside the token and has no
 * league slug in the address, so the rule above reads it as "not in a league"
 * and would bounce it to the league homepage — which is exactly what a captain
 * sees when their link appears to do nothing. The token page is not a league
 * page and must never be redirected like one.
 */
export function homeRedirect(route: Route, home: { slug: string } | null): string | null {
  if (!home) return null;
  if (route.inLeague) return null;
  if (route.captainToken) return null;
  return `#/l/${home.slug}`;
}
